import { inject, Injectable } from '@angular/core';
import { MediaInterface } from '../interfaces/media.interface';
import { SiteInterface } from '../interfaces/site.interface';
import { AuthService } from './auth.service';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { CONSTANTS } from 'src/app/shared/utils/constants';
import { Subject, ReplaySubject, Observable, combineLatest, from, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { FirebaseFCM } from '../interfaces/firebaseFCM.interface';
import { AppleCredentialsInterface } from '../interfaces/apple-credentials.interface';
import { ConfigurationsAppsInterface } from '../interfaces/configurations-apps.interface';
import { HttpClient } from '@angular/common/http';
import { Database, fromRef, list, ListenEvent, ref } from '@angular/fire/database';
import { PermutiveCredentials } from '../interfaces/permutive-credentials.interface';
import { CryptoService } from '../../shared/services/crypto.service';
import { Site } from '../models/site.model';

interface UpdateSitesScriptFeaturesParams {
  siteId: string;
  features: string[];
  permutiveCredentials: PermutiveCredentials;
}

@Injectable()
export class MediaService {
  allMedias: Observable<MediaInterface[]>;
  private readonly firebase = inject(Database);
  private readonly currentSiteSubject: Subject<string> = new ReplaySubject<string>(1); // TODO consider change it to BehaviorSubject.
  private readonly currentSiteTypeSubject: Subject<string> = new ReplaySubject<string>(1); // TODO consider change it to BehaviorSubject.
  private readonly currentGroupSubject: Subject<string> = new ReplaySubject<string>(1); // TODO consider change it to BehaviorSubject.
  private readonly currentGroupTypeSubject: Subject<string> = new ReplaySubject<string>(1); // TODO consider change it to BehaviorSubject.
  private readonly currentGroup$: Observable<string>;
  private readonly currentSite$: Observable<string>;
  private readonly currentSiteType$: Observable<string>;

  constructor(
    private readonly authService: AuthService,
    private readonly fireDataBase: AngularFireDatabase,
    private readonly httpClient: HttpClient,
    private readonly cryptoService: CryptoService
  ) {
    this.currentSite$ = this.currentSiteSubject.asObservable();
    this.changeSite(localStorage.getItem('currentSite'));
    this.currentSiteType$ = this.currentSiteTypeSubject.asObservable();
    this.currentSiteTypeSubject.next(localStorage.getItem('currentSiteType') || 'web');

    this.currentGroup$ = this.currentGroupSubject.asObservable();
    this.changeGroup(localStorage.getItem('currentGroup'));
    this.currentGroupTypeSubject.next(localStorage.getItem('currentGroupType') || 'web');
    this.allMedias = this.authService.user$.pipe(
      switchMap((user) => {
        if (!user) return of([]);
        return this.getMedias();
      }),
      shareReplay(1)
    );
  }

  getCurrentSite(): Observable<string> {
    return this.currentSite$;
  }

  getCurrentSiteType(): Observable<string> {
    return this.currentSiteType$;
  }

  getCurrentGroup(): Observable<string> {
    return this.currentGroup$;
  }

  changeSite(siteId: string): void {
    localStorage.setItem('currentSite', siteId);
    this.currentSiteSubject.next(siteId);
  }

  changeGroup(mediaId: string): void {
    localStorage.setItem('currentGroup', mediaId);
    this.currentGroupSubject.next(mediaId);
  }

  changeSiteType(siteType: string): void {
    localStorage.setItem('currentSiteType', siteType);
    this.currentSiteTypeSubject.next(siteType);
  }

  getSiteFromStorage() {
    return localStorage.getItem('currentSite');
  }

  clearStorage() {
    return localStorage.clear();
  }

  /**
   * @deprecated Use allMedias directly
   */
  getAll(): Observable<MediaInterface[]> {
    return this.allMedias;
  }

  getAllSites(): Observable<SiteInterface[]> {
    return this.fireDataBase.list<SiteInterface>('/sites').valueChanges();
  }

  public getAllAllowed(): Observable<MediaInterface[]> {
    return this.authService.user$.pipe(
      switchMap((user) => {
        if (!user) return of([]);

        return this.getMedias().pipe(
          map((medias) => {
            return medias
              .filter((media: MediaInterface): boolean =>
                media.sites.some((site: SiteInterface): boolean =>
                  user.medias.some((siteId: string): boolean => siteId === site.id)
                )
              )
              .map((media: MediaInterface): MediaInterface => {
                media.sites = media.sites.filter((site: SiteInterface): boolean =>
                  user.medias.some((siteId: string): boolean => siteId === site.id)
                );
                return media;
              });
          })
        );
      })
    );
  }

  getMedias(): Observable<MediaInterface[]> {
    return list(ref(this.firebase, '/medias')).pipe(
      map((changes) => changes.map(({ snapshot }) => snapshot.val())),
      map((medias) =>
        medias
          .filter((media): media is MediaInterface => !!media)
          .map((media) => ({ ...media, sites: (media.sites ?? []).filter((site) => !!site) }))
      )
    );
  }

  getAllActiveMedias(): Observable<MediaInterface[]> {
    return this.getMedias().pipe(
      map((medias) => {
        const user = this.authService.userDetails;
        if (user) {
          return medias
            .filter((media: MediaInterface): boolean =>
              media.sites.some((site: SiteInterface): boolean =>
                user.medias.some((siteId: string): boolean => siteId === site.id)
              )
            )
            .map((media: MediaInterface): MediaInterface => {
              media.sites = media.sites.filter((site: SiteInterface): boolean =>
                user.medias.some((siteId: string): boolean => siteId === site.id)
              );
              return media;
            });
        }
      })
    );
  }

  public getSiteNameById(siteId: string): Observable<string> {
    return this.getSiteById(siteId).pipe(map((site) => site.name));
  }

  getSiteById(siteId: string): Observable<Site> {
    return this.fireDataBase.object<SiteInterface>(`/sites/${siteId}`).valueChanges().pipe(
      map((site) => {
        if (!site) {
          throw new Error('Site not found');
        }
        return new Site(site);
      })
    );
  }

  getGroupById(groupId: string): Observable<MediaInterface> {
    return fromRef(ref(this.firebase, this.getPathOfGroup(groupId)), ListenEvent.value).pipe(
      map(({ snapshot }) => snapshot.val()),
      // Guard against Firebase RTDB null holes inside the sites array (see getMedias).
      map((media) => (media ? { ...media, sites: (media.sites ?? []).filter((site) => !!site) } : media))
    );
  }

  getCurrentSiteInfo(): Observable<SiteInterface> {
    return this.getCurrentSite().pipe(switchMap((sideId) => this.getSiteById(sideId)));
  }

  getCurrentGroupInfo(): Observable<MediaInterface> {//TODO: Revisar donde se usa y sino eliminar
    return this.getCurrentGroup().pipe(switchMap((mediaId) => this.get(mediaId)));
  }

  deleteSite(mediaId: string, siteId: string, media: any) {
    media.sites = media.sites.map((site) => ({
      active: site.active,
      id: site.id,
      name: site.name,
      nameDetail: site.nameDetail,
    }));
    return Promise.all([
      this.updateMedia(mediaId, media),
      this.fireDataBase.object<SiteInterface>(`/sites/${siteId}`).remove(),
    ]);
  }

  get(mediaId: string): Observable<MediaInterface> {
    return this.fireDataBase.object<MediaInterface>(`/medias/${mediaId}`).valueChanges();
  }

  addWithCustomId(mediaId: string, media: MediaInterface) {
    return this.fireDataBase.object<MediaInterface>(`/medias/${mediaId}`).set(media);
  }

  delete(mediaId: string): Promise<void> {
    return this.fireDataBase.object<MediaInterface>(`/medias/${mediaId}`).remove();
  }

  updateMedia(mediaId: string, dataToUpdate: MediaInterface): Promise<void> {
    return this.fireDataBase.object<MediaInterface>(`/medias/${mediaId}`).update(dataToUpdate);
  }

  updateSiteHeaderData(
    mediaId: string,
    siteId: string,
    sitePayload: SiteInterface,
    mediaPayload: SiteInterface[]
  ): Observable<[unknown, unknown]> {
    return combineLatest([
      from(this.fireDataBase.database.ref().child(`/sites/${siteId}`).update(sitePayload)),
      from(this.fireDataBase.database.ref().child(`/medias/${mediaId}/sites`).update(mediaPayload)),
    ]);
  }

  updateSitePushNotificationDefaultData(siteId: string, payload: FirebaseFCM): Observable<void> {
    return from(this.fireDataBase.database.ref().child(`/sites/${siteId}/certificateFCM`).update(payload));
  }

  updateSitePushNotificationSafariData(siteId: string, payload: AppleCredentialsInterface): Observable<void> {
    return from(this.fireDataBase.database.ref().child(`/sites/${siteId}/appleCredentials`).update(payload));
  }

  updateSitePrometeoData(siteId: string, payload: any): Observable<void> { //Revisar si se usa y eliminar
    return from(this.fireDataBase.database.ref().child(`/sites/${siteId}/prometeoCredentials`).update(payload));
  }

  getMediaParentBySiteId(siteId) {
    return this.getAll().pipe(
      map((medias) => {
        return medias.find((media) => media.sites.some((site) => site.id === siteId));
      })
    );
  }

  updateAppData(siteId: string, credentials: ConfigurationsAppsInterface): Observable<void> {
    return from(this.fireDataBase.database.ref().child(`/sites/${siteId}/configurationApps`).update(credentials));
  }

  updateSitesJavascriptWall(siteId: string, form: any): Observable<void> {
    return from(this.fireDataBase.database.ref().child(this.javascriptWallPath(siteId)).update(form));
  }

  getSitesJavascriptWall(siteId: string): Observable<any> {
    return this.fireDataBase.object<any>(this.javascriptWallPath(siteId)).valueChanges();
  }

  getMediasJavascriptWall(mediaId: string): Observable<any> {
    return this.fireDataBase.object<any>(`/medias/${mediaId}/javascriptWall`).valueChanges();
  }

  updateSitesTypeWall(siteId: any, javascript: any): Observable<any> {
    const body = {
      siteId: siteId,
      javascript: javascript,
    };
    return this.httpClient.post<any>(CONSTANTS.API_REST_UPDATE_SITES_TYPE_WALL, body);
  }

  updateSitesScriptFeatures({
    siteId,
    features,
    permutiveCredentials,
  }: UpdateSitesScriptFeaturesParams): Observable<void> {
    const url = CONSTANTS.API_REST_UPDATE_SITES_SCRIPT_FEATURES.replace(':mediaId', siteId);
    const body = {
      features,
      permutive: this.cryptoService.encrypt({
        key: CONSTANTS.AES_KEY,
        data: permutiveCredentials,
      })
    };
    return this.httpClient.post<void>(url, body);
  }

  updateMediasTypeWall(siteIds: string[], javascript: string): Observable<string> {
    const body = {
      siteIds: siteIds,
      javascript: javascript,
    };
    return this.httpClient.post<any>(CONSTANTS.API_REST_UPDATE_MEDIAS_TYPE_WALL, body);
  }

  getListOfSite() {
    return this.getAllSites().pipe(
      map((site: Array<any>) => site.map((value) => ({ id: value.id, name: value.name })))
    );
  }

  getIdAndNameOfSites(): Observable<Array<{ id: string; name: string }>> {
    return this.getAllSites().pipe(
      map((site: Array<SiteInterface>) => site.map((value) => ({ id: value.id, name: value.name })))
    );
  }

  updateGigyaData(siteId: string, form: any): Observable<void> {
    return from(this.fireDataBase.database.ref().child(`/sites/${siteId}/gigyaConfig`).update(form));
  }

  private javascriptWallPath(siteId: string): string {
    return `/sites/${siteId}/javascriptWall`;
  }

  private getPathOfGroup(groupId: string) {
    return ['medias', groupId].join('/');
  }
}
